import * as Sentry from '@sentry/react';
import { selectIsSystemSession } from 'features/application/selectors';
import { store } from 'store/store';
import { emitTelemetryEvent } from 'utils/sentry/telemetryEvent';
import { RejectedJoinCode } from 'utils/webrtc/subscribeGrantError';

export type RejoinGrantOutcome = 'refreshed' | 'token_refresh_failed';

export interface RejoinGrantAttributes {
  outcome: RejoinGrantOutcome;
  code: RejectedJoinCode;
  handleReset: boolean;
  error: string | null;
}

export interface SubscribeRefusedAttributes {
  code: number;
  handle: string;
  streamCount: number;
}

const REJOIN_GRANT_MESSAGE = '[Videoroom Receiver] rejoin on fresh grant';
const SUBSCRIBE_REFUSED_MESSAGE = '[Videoroom Receiver] subscribe refused';

export const emitRejoinGrant = (attributes: RejoinGrantAttributes) =>
  emitTelemetryEvent({
    event: 'subscribe.rejoin',
    message: `${REJOIN_GRANT_MESSAGE} — ${attributes.outcome}`,
    level: attributes.outcome === 'refreshed' ? 'info' : 'warn',
    channel: 'streaming',
    cap: 20,
    attributes: { ...attributes, isSystemSession: selectIsSystemSession(store.getState()) },
  });

// Sentry scrubs the rejection string to [Filtered], so the code travels as its own attribute and tag.
export const emitSubscribeRefused = (attributes: SubscribeRefusedAttributes, cause: unknown) => {
  emitTelemetryEvent({
    event: 'subscribe.refused',
    message: SUBSCRIBE_REFUSED_MESSAGE,
    level: 'error',
    channel: 'streaming',
    attributes: { ...attributes, isSystemSession: selectIsSystemSession(store.getState()) },
  });

  Sentry.captureException(new Error(SUBSCRIBE_REFUSED_MESSAGE, { cause }), {
    fingerprint: ['subscribe-refused'],
    tags: { component: 'VideoroomReceiver', operation: 'subscribe', code: String(attributes.code) },
    contexts: { subscribeRefused: { ...attributes } },
  });
};

// `core` is the token check refusing a spent grant (405); `plugin` is the streaming plugin's own error event.
export type StreamingRestartRefusal = 'core' | 'plugin';

export interface StreamingRestartRefusedAttributes {
  refusal: StreamingRestartRefusal;
  code: number | null;
  handle: string;
}

const STREAMING_RESTART_REFUSED_MESSAGE = '[Streaming Receiver] restart refused';

// No exception of its own: the Rejoin it leads to already raises one through reportMediaFailure.
export const emitStreamingRestartRefused = (attributes: StreamingRestartRefusedAttributes) =>
  emitTelemetryEvent({
    event: 'streaming.restart.refused',
    message: `${STREAMING_RESTART_REFUSED_MESSAGE} — ${attributes.refusal}`,
    level: 'error',
    channel: 'streaming',
    attributes: { ...attributes, isSystemSession: selectIsSystemSession(store.getState()) },
  });
